import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import type { ActivityPage, ActivitySummary } from "@nebulize/sdk";

import { Button, EmptyState, ErrorState, Panel, Skeleton, StatusBadge } from "@/components/ui";
import { Paged } from "@/features/console/components/Paged";
import { ApiError, api } from "@/lib/api";

import { MarketingShell } from "./MarketingShell";

const PAGE = 200;

function Listing({ activity }: { activity: ActivitySummary }) {
  return (
    <article className="mk-card">
      <div className="mk-card-top">
        <div>
          <h3>{activity.name}</h3>
          <p className="mk-by">
            {activity.activity_id} {activity.version}
          </p>
        </div>
        {activity.is_bundle === true ? <StatusBadge tone="info">Bundle</StatusBadge> : null}
      </div>
      {activity.description === undefined || activity.description === "" ? null : (
        <p className="mk-card-body">{activity.description}</p>
      )}
    </article>
  );
}

function matches(activity: ActivitySummary, needle: string): boolean {
  return `${activity.name} ${activity.activity_id}`.toLowerCase().includes(needle);
}

/** The published catalog, read live. Signed out it is not readable, and it says so. */
function Catalog() {
  const catalog = useQuery({
    queryKey: ["catalog", "activities"],
    retry: false,
    queryFn: () => api.get<ActivityPage>(`/v1/activities?limit=${String(PAGE)}`),
  });

  if (catalog.isPending) return <Skeleton height={120} />;

  if (catalog.error instanceof ApiError && catalog.error.status === 401) {
    return (
      <EmptyState
        bare
        headline="Sign in to browse the catalog"
        action={
          <Link to="/sign-in">
            <Button>Sign in</Button>
          </Link>
        }
      />
    );
  }

  if (catalog.error !== null) return <ErrorState headline="Could not load the catalog" />;

  const activities = catalog.data.activities ?? [];
  if (activities.length === 0) return <EmptyState bare headline="Nothing published yet" />;

  return (
    <Paged rows={activities} pageSize={12} placeholder="Find an activity" match={matches}>
      {(page) => (
        <div className="mk-cards">
          {page.map((activity) => (
            <Listing key={activity.activity_id} activity={activity} />
          ))}
        </div>
      )}
    </Paged>
  );
}

/** The public catalog, per `docs/ui/marketing/activity-catalog.md`. */
export function Activities() {
  return (
    <MarketingShell>
      <section className="mk-page">
        <p className="mk-eyebrow">Activities</p>
        <h1 className="mk-h1">What you can run</h1>
        <p className="mk-lede">
          Every activity brings its own canvases, its own settings and its own way for people to
          take part.
        </p>
      </section>

      <section className="mk-section">
        <Panel>
          <Catalog />
        </Panel>
      </section>

      <section className="mk-section">
        <h2 className="mk-h2">Running one of these</h2>
        <p className="mk-body">
          Plans are tiered by concurrent participants, and every tier carries every published
          activity.
        </p>
        <div className="mk-cta">
          <Link to="/pricing">
            <Button>See pricing</Button>
          </Link>
          <Link to="/publish">
            <Button variant="ghost">Build an activity</Button>
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
